/**
 * Enhanced Error Boundary with Sentry Integration
 *
 * Provides comprehensive error handling and automatic error reporting.
 */

import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import React from "react";

interface ErrorFallbackProps {
  readonly error: Error;
  readonly resetError: () => void;
}

interface AppErrorBoundaryProps {
  readonly children: React.ReactNode;
}

/**
 * Error fallback component with user-friendly interface
 */
function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const handleGoHome = () => {
    globalThis.location.href = "/";
  };

  const handleRetry = () => {
    resetError();
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center space-y-6">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-white">Oops! Something went wrong</h1>
          <p className="text-gray-400">We've encountered an unexpected error. Our team has been notified.</p>
        </div>

        {/* Error Details (Development) */}
        {import.meta.env.MODE === "development" && (
          <div className="bg-gray-900 p-4 rounded-lg text-left">
            <h3 className="text-sm font-semibold text-red-400 mb-2">Error Details</h3>
            <code className="text-xs text-gray-300 break-all">{error.message}</code>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleRetry}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-lumi-accent text-black font-medium rounded-lg hover:bg-lumi-accent/90 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>

          <button
            onClick={handleGoHome}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * App-level error boundary with Sentry integration
 */
const AppErrorBoundaryFallback = ({ error, resetError }: { error: unknown; resetError: () => void }) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  return <ErrorFallback error={errorObj} resetError={resetError} />;
};

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <SentryErrorBoundary
      fallback={AppErrorBoundaryFallback}
      beforeCapture={(scope) => {
        scope.setTag("errorBoundary", true);
        scope.setContext("page", {
          url: globalThis.location?.href,
          title: document?.title,
          referrer: document?.referrer,
        });
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}

/**
 * Minimal error fallback for component-level errors
 */
function MinimalErrorFallback({ resetError }: { readonly resetError: () => void }) {
  return (
    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
      <div className="flex items-center gap-2 text-red-400 mb-2">
        <AlertTriangle className="w-4 h-4" />
        <span className="font-medium">Component Error</span>
      </div>
      <p className="text-sm text-gray-400 mb-3">This component encountered an error and couldn't render properly.</p>
      <button
        onClick={resetError}
        className="text-sm bg-red-500/20 hover:bg-red-500/30 text-red-300 px-3 py-1 rounded transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

/**
 * Component-level error boundary for isolated error handling
 */
interface ComponentErrorBoundaryProps {
  readonly children: React.ReactNode;
  readonly name?: string;
}

const ComponentErrorBoundaryFallback = ({ resetError }: { resetError: () => void }) => (
  <MinimalErrorFallback resetError={resetError} />
);

export function ComponentErrorBoundary({ children, name }: ComponentErrorBoundaryProps) {
  return (
    <SentryErrorBoundary
      fallback={ComponentErrorBoundaryFallback}
      beforeCapture={(scope) => {
        scope.setTag("errorBoundary", true);
        scope.setTag("component", name || "unknown");
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}
